import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DAY_MSEC,
  getIntRegExp,
  HOUR_MSEC,
  MAX_DAYS,
  MAX_HOURS,
  MAX_MINUTES,
  MAX_MSEC,
  MINUTE_MSEC,
  PERIOD_TYPES,
  WEEK_MSEC,
} from 'constants/common';
import { appReactMemo } from 'hocs';
import { convertMilliseconds } from 'utils/common';
import { INullable } from 'utils/types';

import HintButton from '../../HintButton';

import { INPUT_MODES } from './helpers';
import {
  FieldColumn,
  HintButtonContainer,
  HintText,
  Label,
  NameContainer,
  StyledInput,
  TimeFields,
} from './styled';

export type ITimeChange = (payload: { msecs?: number }) => void;

interface IFieldValues {
  numDays: INullable<number>;
  numHours: INullable<number>;
  numMinutes: INullable<number>;
  numWeeks: INullable<number>;
}

interface IPeriodInputsProps {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  hideLabel?: boolean;
  hintText?: string;
  inputsMode?: INPUT_MODES;
  isAutoFocused?: boolean;
  label?: string;
  maxValue?: number;
  onBlur?: () => void;
  onTimeChange: ITimeChange;
  sizeVariant: 'lg' | 'sm';
  value?: number;
}

const INITIAL_DATA = { days: '', hours: '', minutes: '', weeks: '' };

const PeriodInputs = ({
  className,
  disabled,
  error,
  hideLabel,
  hintText,
  inputsMode = INPUT_MODES.HOUR_MINUTE,
  isAutoFocused,
  label,
  maxValue = MAX_MSEC,
  onBlur,
  onTimeChange,
  sizeVariant,
  value,
}: IPeriodInputsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.time' });

  const {
    days: maxDays,
    hours: maxHours,
    minutes: maxMinutes,
    weeks: maxWeeks,
  } = React.useMemo(() => convertMilliseconds(maxValue), [maxValue]);

  const [data, setDate] = React.useState<{
    days: string;
    hours: string;
    minutes: string;
    weeks: string;
  }>({ ...INITIAL_DATA });

  const saveChanges = React.useCallback(
    ({
      days,
      hours,
      minutes,
      weeks,
    }: {
      days: string;
      hours: string;
      minutes: string;
      weeks: string;
    }) => {
      const numMinutes = Number(minutes);
      const numHours = Number(hours);
      const numDays = Number(days);
      const numWeeks = Number(weeks);

      const msecs =
        numWeeks * WEEK_MSEC +
        numDays * DAY_MSEC +
        numHours * HOUR_MSEC +
        numMinutes * MINUTE_MSEC;

      onTimeChange({ msecs: msecs || undefined });
    },
    [onTimeChange],
  );

  const setValues = React.useCallback(
    ({ numDays, numHours, numMinutes, numWeeks }: IFieldValues) => {
      const weeks = Number(numWeeks);
      const days = Number(numDays);
      const hours = Number(numHours);
      const minutes = Number(numMinutes);

      let saveData = {
        days: days ? days.toString() : '',
        hours: hours ? hours.toString() : '',
        minutes: minutes ? minutes.toString() : '',
        weeks: weeks ? weeks.toString() : '',
      };

      if (inputsMode === INPUT_MODES.DAY_HOUR_MINUTE) {
        saveData = {
          days: weeks || days ? (weeks * 7 + days).toString() : '',
          hours: hours ? hours.toString() : '',
          minutes: minutes ? minutes.toString() : '',
          weeks: '',
        };
      } else if (inputsMode === INPUT_MODES.HOUR_MINUTE) {
        saveData = {
          days: '',
          hours:
            weeks || days || hours ? ((weeks * 7 + days) * 24 + hours).toString() : '',
          minutes: minutes ? minutes.toString() : '',
          weeks: '',
        };
      }

      setDate(saveData);
      saveChanges(saveData);
    },
    [inputsMode, saveChanges],
  );

  React.useEffect(() => {
    if (!value) {
      setDate({ ...INITIAL_DATA });
    } else {
      const {
        days: numDays,
        hours: numHours,
        minutes: numMinutes,
        weeks: numWeeks,
      } = convertMilliseconds(value);

      setValues({ numDays, numHours, numMinutes, numWeeks });
    }
  }, [value]);

  const onChangePeriod = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: PERIOD_TYPES) => {
      const { value } = e.target;
      const newValue = Number(value);

      const { days, hours, minutes, weeks } = data;
      const numMinutes = Number(minutes);
      const numHours = Number(hours);
      const numDays = Number(days);
      const numWeeks = Number(weeks);

      let newResultValue =
        numWeeks * WEEK_MSEC +
        newValue * DAY_MSEC +
        numHours * HOUR_MSEC +
        numMinutes * MINUTE_MSEC;

      if (type === PERIOD_TYPES.HOUR) {
        newResultValue =
          numWeeks * WEEK_MSEC +
          numDays * DAY_MSEC +
          newValue * HOUR_MSEC +
          numMinutes * MINUTE_MSEC;
      } else if (type === PERIOD_TYPES.WEEK) {
        newResultValue =
          newValue * WEEK_MSEC +
          numDays * DAY_MSEC +
          numHours * HOUR_MSEC +
          numMinutes * MINUTE_MSEC;
      } else if (type === PERIOD_TYPES.MINUTE) {
        newResultValue =
          numWeeks * WEEK_MSEC +
          numDays * DAY_MSEC +
          numHours * HOUR_MSEC +
          newValue * MINUTE_MSEC;
      }

      if (newResultValue > maxValue) {
        return setValues({
          numDays: maxDays,
          numHours: maxHours,
          numMinutes: maxMinutes,
          numWeeks: maxWeeks,
        });
      }

      let newDate = { ...data, weeks: value };

      if (type === PERIOD_TYPES.DAY) {
        newDate = {
          ...data,
          days:
            inputsMode === INPUT_MODES.WEEK_DAY_HOUR_MINUTE && newValue > MAX_DAYS
              ? MAX_DAYS.toString()
              : value,
        };
      } else if (type === PERIOD_TYPES.HOUR) {
        const isMaxHours =
          (inputsMode === INPUT_MODES.DAY_HOUR_MINUTE ||
            inputsMode === INPUT_MODES.WEEK_DAY_HOUR_MINUTE) &&
          newValue > MAX_HOURS;

        newDate = { ...data, hours: isMaxHours ? MAX_HOURS.toString() : value };
      } else if (type === PERIOD_TYPES.MINUTE) {
        newDate = {
          ...data,
          minutes: newValue > MAX_MINUTES ? MAX_MINUTES.toString() : value,
        };
      }

      setDate(newDate);
      saveChanges(newDate);
    },
    [data, maxValue, inputsMode, setValues, saveChanges],
  );

  const onChange = React.useCallback(
    (type: PERIOD_TYPES) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => onChangePeriod(e, type);
    },
    [onChangePeriod],
  );

  // Without a label there is nothing to reserve a line for, so the name row is dropped
  // entirely rather than rendered empty above the inputs.
  const showLabel = !hideLabel && !!label;

  return (
    <div className={className}>
      {(showLabel || !!hintText) && (
        <NameContainer>
          {showLabel && (
            <Label size={sizeVariant === 'sm' ? 'xs' : 'sm'} variant='bold'>
              {label}
            </Label>
          )}
          {hintText && (
            <HintButtonContainer>
              <HintButton>{hintText}</HintButton>
            </HintButtonContainer>
          )}
        </NameContainer>
      )}
      <TimeFields>
        {inputsMode === INPUT_MODES.WEEK_DAY_HOUR_MINUTE && (
          <FieldColumn>
            <StyledInput
              disabled={disabled}
              error={error}
              isAutoFocused={
                isAutoFocused && inputsMode === INPUT_MODES.WEEK_DAY_HOUR_MINUTE
              }
              onBlur={onBlur}
              onChange={onChange(PERIOD_TYPES.WEEK)}
              placeholder={t('ww')}
              regExp={getIntRegExp(0, 4)}
              sizeVariant={sizeVariant}
              value={data.weeks}
            />
            <HintText size='xs'>{t('weeks')}</HintText>
          </FieldColumn>
        )}
        {(inputsMode === INPUT_MODES.DAY_HOUR_MINUTE ||
          inputsMode === INPUT_MODES.WEEK_DAY_HOUR_MINUTE) && (
          <FieldColumn>
            <StyledInput
              disabled={disabled}
              error={error}
              isAutoFocused={isAutoFocused && inputsMode === INPUT_MODES.DAY_HOUR_MINUTE}
              onBlur={onBlur}
              onChange={onChange(PERIOD_TYPES.DAY)}
              placeholder={t('dd')}
              regExp={getIntRegExp(0, 4)}
              sizeVariant={sizeVariant}
              value={data.days}
            />
            <HintText size='xs'>{t('days')}</HintText>
          </FieldColumn>
        )}
        <FieldColumn>
          <StyledInput
            disabled={disabled}
            error={error}
            isAutoFocused={isAutoFocused && inputsMode === INPUT_MODES.HOUR_MINUTE}
            onBlur={onBlur}
            onChange={onChange(PERIOD_TYPES.HOUR)}
            placeholder={t('hh')}
            regExp={getIntRegExp(0, 4)}
            sizeVariant={sizeVariant}
            value={data.hours}
          />
          <HintText size='xs'>{t('hours')}</HintText>
        </FieldColumn>
        <FieldColumn>
          <StyledInput
            disabled={disabled}
            error={error}
            isAutoFocused={isAutoFocused && inputsMode === INPUT_MODES.HOUR_MINUTE}
            onBlur={onBlur}
            onChange={onChange(PERIOD_TYPES.MINUTE)}
            placeholder={t('mm')}
            regExp={getIntRegExp()}
            sizeVariant={sizeVariant}
            value={data.minutes}
          />
          <HintText size='xs'>{t('minutes')}</HintText>
        </FieldColumn>
      </TimeFields>
    </div>
  );
};

export default appReactMemo(PeriodInputs);
